/**
 * TCOLogo Component
 *
 * Логотип TCO (Time Control Office) для административной панели
 * Поддерживает 3 варианта отображения и 4 цветовые схемы
 */

import { useMemo } from 'react';

// Цветовые схемы логотипа
export type ColorScheme = 'blue' | 'purple' | 'green' | 'orange';

// Варианты отображения
export type LogoVariant = 'full' | 'icon' | 'text';

interface TCOLogoProps {
  variant?: LogoVariant;
  colorScheme?: ColorScheme;
  className?: string;
  width?: number;
  height?: number;
}

const colors = {
  blue: {
    primary: '#2563eb',
    secondary: '#3b82f6',
    accent: '#60a5fa',
    dark: '#1e40af',
    gray: '#E5E7EB',
  },
  purple: {
    primary: '#7c3aed',
    secondary: '#8b5cf6',
    accent: '#a78bfa',
    dark: '#5b21b6',
    gray: '#E5E7EB',
  },
  green: {
    primary: '#059669',
    secondary: '#10b981',
    accent: '#34d399',
    dark: '#047857',
    gray: '#E5E7EB',
  },
  orange: {
    primary: '#ea580c',
    secondary: '#f97316',
    accent: '#fb923c',
    dark: '#c2410c',
    gray: '#E5E7EB',
  },
};

/**
 * Полный логотип с иконкой и текстом
 * Используется на страницах авторизации, splash screen
 */
function FullLogo({ colorScheme = 'blue' }: { colorScheme: ColorScheme }) {
  const currentColors = colors[colorScheme];
  const gradientId = `tco-gradient-full-${colorScheme}`;

  return (
    <svg width="360" height="100" viewBox="0 0 360 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={currentColors.primary} />
          <stop offset="100%" stopColor={currentColors.secondary} />
        </linearGradient>
      </defs>

      {/* Иконка - часы с прогрессом */}
      <g transform="translate(10, 10)">
        {/* Внешнее кольцо (фон) */}
        <circle cx="40" cy="40" r="35" stroke={currentColors.gray} strokeWidth="5" fill="none"/>
        
        {/* Внешнее кольцо (прогресс ~75%) */}
        <circle cx="40" cy="40" r="35" 
                stroke={`url(#${gradientId})`}
                strokeWidth="5" 
                fill="none" 
                strokeDasharray="165 220" 
                strokeLinecap="round" 
                transform="rotate(-90 40 40)"/>
        
        {/* Внутренний круг с часами */}
        <circle cx="40" cy="40" r="23" fill={`url(#${gradientId})`}/>
        
        {/* Центр часов */}
        <circle cx="40" cy="40" r="2.5" fill="white"/>
        
        {/* Часовая стрелка */}
        <line x1="40" y1="40" x2="40" y2="28" stroke="white" strokeWidth="3.5" strokeLinecap="round"/>
        
        {/* Минутная стрелка */}
        <line x1="40" y1="40" x2="53" y2="47" stroke="white" strokeWidth="2.8" strokeLinecap="round"/>
      </g>

      {/* Текст TCO */}
      <text
        x="105"
        y="58"
        fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        fontWeight="800"
        fontSize="42"
        fill={currentColors.dark}
        letterSpacing="-1"
      >
        TCO
      </text>

      {/* Тагline */}
      <text
        x="105"
        y="82"
        fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        fontWeight="400"
        fontSize="13"
        fill="#64748b"
        letterSpacing="1.5"
      >
        TIME CONTROL OFFICE
      </text>
    </svg>
  );
}

/**
 * Только иконка (для приложения)
 * Используется в sidebar, navbar, favicon
 */
function IconOnly({ colorScheme = 'blue' }: { colorScheme: ColorScheme }) {
  const currentColors = colors[colorScheme];
  const gradientId = `tco-gradient-icon-${colorScheme}`;

  return (
    <svg width="80" height="80" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={currentColors.primary} />
          <stop offset="100%" stopColor={currentColors.secondary} />
        </linearGradient>
      </defs>
      
      {/* Внешнее кольцо (фон) */}
      <circle cx="256" cy="256" r="220" stroke={currentColors.gray} strokeWidth="30" fill="none"/>
      
      {/* Внешнее кольцо (прогресс ~75%) */}
      <circle cx="256" cy="256" r="220" 
              stroke={`url(#${gradientId})`}
              strokeWidth="30" 
              fill="none" 
              strokeDasharray="1036 1382" 
              strokeLinecap="round" 
              transform="rotate(-90 256 256)"/>
      
      {/* Внутренний круг с часами */}
      <circle cx="256" cy="256" r="145" fill={`url(#${gradientId})`}/>
      
      {/* Центр часов */}
      <circle cx="256" cy="256" r="16" fill="white"/>
      
      {/* Часовая стрелка */}
      <line x1="256" y1="256" x2="256" y2="158" stroke="white" strokeWidth="22" strokeLinecap="round"/>
      
      {/* Минутная стрелка */}
      <line x1="256" y1="256" x2="340" y2="300" stroke="white" strokeWidth="18" strokeLinecap="round"/>
    </svg>
  );
}

/**
 * Минимальный - только название
 * Используется в header, компактных местах
 */
function TextOnly({ colorScheme = 'blue' }: { colorScheme: ColorScheme }) {
  const currentColors = colors[colorScheme];

  return (
    <svg width="200" height="50" viewBox="0 0 200 50" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text
        x="0"
        y="36"
        fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        fontWeight="800"
        fontSize="36"
        fill={currentColors.dark}
        letterSpacing="-1"
      >
        TCO
      </text>
      <text
        x="95"
        y="36"
        fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        fontWeight="300"
        fontSize="14"
        fill="#64748b"
      >
        Time Control
      </text>
    </svg>
  );
}

/**
 * Главный экспортируемый компонент
 *
 * @example
 * // Полный логотип на странице авторизации
 * <TCOLogo variant="full" colorScheme="blue" />
 *
 * @example
 * // Иконка в sidebar
 * <TCOLogo variant="icon" colorScheme="blue" width={48} />
 *
 * @example
 * // Только текст в header
 * <TCOLogo variant="text" colorScheme="purple" />
 */
export function TCOLogo({
  variant = 'full',
  colorScheme = 'blue',
  className = '',
  width,
  height
}: TCOLogoProps) {
  const LogoComponent = useMemo(() => {
    switch (variant) {
      case 'icon':
        return IconOnly;
      case 'text':
        return TextOnly;
      case 'full':
      default:
        return FullLogo;
    }
  }, [variant]);

  const style = useMemo(() => {
    const baseStyle: React.CSSProperties = {};
    if (width) baseStyle.width = width;
    if (height) baseStyle.height = height;
    return baseStyle;
  }, [width, height]);

  return (
    <div className={className} style={style}>
      <LogoComponent colorScheme={colorScheme} />
    </div>
  );
}

// Экспортируем также старое название для обратной совместимости
export { TCOLogo as WeekappLogo };

/**
 * Компактный логотип для Sidebar (темный фон)
 * Иконка часов + текст TCO белыми буквами
 */
export function TCOSidebarLogo() {
  return (
    <div className="flex items-center gap-3 group cursor-pointer">
      {/* Компактная иконка часов с прогрессом */}
      <div className="transition-transform duration-300 group-hover:scale-110">
        <svg width="40" height="40" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="sidebar-tco-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>

          {/* Внешнее кольцо (фон) */}
          <circle cx="40" cy="40" r="34" stroke="#4B5563" strokeWidth="4" fill="none"/>

          {/* Внешнее кольцо (прогресс ~75%) */}
          <circle cx="40" cy="40" r="34"
                  stroke="url(#sidebar-tco-gradient)"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray="160 214"
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"/>

          {/* Внутренний круг с часами */}
          <circle cx="40" cy="40" r="22" fill="url(#sidebar-tco-gradient)"/>

          {/* Центр часов */}
          <circle cx="40" cy="40" r="2.5" fill="white"/>

          {/* Часовая стрелка */}
          <line x1="40" y1="40" x2="40" y2="28" stroke="white" strokeWidth="3" strokeLinecap="round"/>

          {/* Минутная стрелка */}
          <line x1="40" y1="40" x2="52" y2="46" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Текст TCO ADMIN */}
      <h1 className="text-2xl tracking-tight transition-all duration-300">
        <span className="font-extrabold text-white" style={{ letterSpacing: '-0.5px' }}>TCO</span>
        <span className="font-extrabold text-blue-300 ml-1" style={{ letterSpacing: '-0.5px' }}>ADMIN</span>
      </h1>
    </div>
  );
}


